import React from 'react';
import { ollamaApi, OllamaChatMessage } from '../../services/api';
import styles from './MarkdownEditor.module.css';

interface AiChatInterfaceProps {
  showAiChat: boolean;
  ollamaEnabled: boolean;
  aiInput: string;
  setAiInput: (input: string) => void;
  aiMessages: OllamaChatMessage[];
  setAiMessages: (messages: OllamaChatMessage[]) => void;
  isAiGenerating: boolean;
  setIsAiGenerating: (isGenerating: boolean) => void;
  setError: (error: string) => void;
  setShowAiChat: (show: boolean) => void;
  setContent: (content: string) => void;
  mode: 'create' | 'edit';
  documentId?: number;
  title: string;
  content: string;
  folderPath: string;
  isPrivate: boolean;
  tags: string[];
  navigation?: (url: string) => void;
}

export const AiChatInterface: React.FC<AiChatInterfaceProps> = ({
  ollamaEnabled,
  aiInput,
  setAiInput,
  aiMessages,
  setAiMessages,
  isAiGenerating,
  setIsAiGenerating,
  setError,
  setShowAiChat,
  setContent,
  mode,
  documentId,
  title,
  content,
  folderPath,
  isPrivate,
  tags,
  navigation,
}) => {
  const handleAiSendMessage = async () => {
    if (!aiInput.trim()) return;
    
    const userMessage: OllamaChatMessage = { role: 'user', content: aiInput };
    const newMessages = [...aiMessages, userMessage];
    setAiMessages(newMessages);
    setAiInput('');
    setIsAiGenerating(true);
    setError('');
    
    try {
      const response = await ollamaApi.chat({ messages: newMessages });
      const aiMessage: OllamaChatMessage = { role: 'assistant', content: response.data.response };
      setAiMessages([...newMessages, aiMessage]);
    } catch (err: any) {
      console.error('AI chat error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to communicate with AI';
      setError(errorMessage);
    } finally {
      setIsAiGenerating(false);
    }
  };

  const handleAiGenerateDocument = async () => {
    if (!aiInput.trim()) return;
    
    setIsAiGenerating(true);
    setError('');
    
    try {
      const response = await ollamaApi.generate({ 
        prompt: mode === 'edit' ? `Please modify the document according to your request and return the ENTIRE document with all changes applied. Keep all existing content that wasn't modified.` : aiInput,
        context: mode === 'edit' ? `Current document title: ${title}. Current full content:\n\n${content}` : undefined 
      });
      
      if (mode === 'create') {
        setContent(response.data.markdown);
        setAiInput('');
        setAiMessages([]);
      } else if (mode === 'edit' && documentId) {
        setContent(response.data.markdown);
        setAiInput('');
        setAiMessages([]);
      }
      
      setShowAiChat(false);
    } catch (err: any) {
      console.error('AI generation error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to generate content with AI';
      setError(errorMessage);
    } finally {
      setIsAiGenerating(false);
    }
  };

  const handleAiCreateDocument = async () => {
    if (!aiInput.trim()) return;
    
    setIsAiGenerating(true);
    setError('');
    
    try {
      const response = await ollamaApi.createDocument({ 
        prompt: aiInput,
        title: title || undefined,
        folder_path: folderPath || undefined,
        is_private: isPrivate || undefined,
        tags: tags.length > 0 ? tags : undefined
      });
      
      setAiInput('');
      setAiMessages([]);
      setShowAiChat(false);
      
      if (response.data && response.data.slug) {
        if (navigation) {
          setTimeout(() => navigation(`/docs/${response.data.slug}`), 500);
        }
      }
    } catch (err: any) {
      console.error('AI create document error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to create document with AI';
      setError(errorMessage);
    } finally {
      setIsAiGenerating(false);
    }
  };

  if (!ollamaEnabled) {
    return null;
  }

  return (
    <div style={{ padding: '20px', backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: '8px', marginBottom: '15px' }}>
      <div style={{ marginBottom: '10px' }}>
        <label className={styles.formLabel} style={{ display: 'block', marginBottom: '8px' }}>
          {mode === 'create' ? 'Describe your document' : 'Continue or modify your document'}
        </label>
        <textarea
          className={styles.formInput}
          value={aiInput}
          onChange={(e) => setAiInput(e.target.value)}
          placeholder="e.g., Write a comprehensive guide about Ollama integration..."
          rows={4}
          disabled={isAiGenerating}
        />
        <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
          <button
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={mode === 'create' ? handleAiCreateDocument : handleAiGenerateDocument}
            disabled={isAiGenerating || !aiInput.trim()}
            style={{ flex: 1 }}
          >
            {isAiGenerating ? 'Thinking...' : mode === 'create' ? 'Create with AI' : 'Generate Content'}
          </button>
        </div>
      </div>
      
      {aiMessages.length > 0 && (
        <div style={{ borderTop: '1px solid #ddd', paddingTop: '15px', marginTop: '15px' }}>
          <label className={styles.formLabel} style={{ display: 'block', marginBottom: '8px' }}>
            Conversation
          </label>
          <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '10px' }}>
            {aiMessages.map((msg, idx) => (
              <div
                key={idx}
                style={{
                  padding: '10px',
                  marginBottom: '8px',
                  borderRadius: '6px',
                  backgroundColor: msg.role === 'user' ? 'var(--accent)' : '#f0f0f0',
                  color: msg.role === 'user' ? 'white' : 'black'
                }}
              >
                <strong>{msg.role === 'user' ? 'You' : 'AI'}:</strong>
                <div style={{ marginTop: '4px', whiteSpace: 'pre-wrap' }}>{msg.content}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input
              type="text"
              className={styles.formInput}
              value={aiInput}
              onChange={(e) => setAiInput(e.target.value)}
              placeholder="Continue the conversation..."
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleAiSendMessage();
                }
              }}
              disabled={isAiGenerating}
              style={{ flex: 1, marginRight: '10px' }}
            />
            <button
              className={`${styles.btn} ${styles.btnSecondary}`}
              onClick={handleAiSendMessage}
              disabled={isAiGenerating || !aiInput.trim()}
            >
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
